/** @format */

import DownloadIcon from "@mui/icons-material/Download";
import PrintIcon from "@mui/icons-material/Print";
import { Button, Stack } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { useApiFeedback } from "../app/useApiFeedback";
import { HasErrors } from "../common/Logic/ServerResponse";
import MessageResponse from "../common/feedback/messageResponse";
import { fetchDashboard } from "../features/admin/adminApi";
import { selectAdmin } from "../features/admin/adminSlice";
import ApplicationStatusReport from "../features/admin/components/Dashboard/ApplicationStatusReport";
import "../features/admin/components/Dashboard/dashboard-print.css";
import DisplayCharts from "../features/admin/components/Dashboard/DisplayCharts";
import { exportDashboardSnapshotCsv } from "../features/admin/components/Dashboard/exportDashboardCsv";
import { DasboardDto } from "../features/admin/models";
import {
  fetchApplicationYearsThunk,
  fetchDashboardThunk,
} from "../features/admin/thunks";

function Dashboard() {
  const dispatch = useAppDispatch();
  const { feedback, handleApiResponse } = useApiFeedback();
  const admin = useAppSelector(selectAdmin);
  const dashboardData = admin.dashboardState.dashboard;
  const dashStatus = admin.dashboardState.status;

  const [dashboardYear, setDashboardYear] = useState(() =>
    new Date().getFullYear()
  );
  const [priorYearData, setPriorYearData] = useState<DasboardDto | null>(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
  const priorYearSeq = useRef(0);

  const loadPriorYear = useCallback(async () => {
    const seq = ++priorYearSeq.current;
    try {
      const res = await fetchDashboard(dashboardYear - 1);
      if (seq !== priorYearSeq.current) return;
      const ok =
        HasErrors({ payload: res }) === 2 || HasErrors({ payload: res }) === 3;
      setPriorYearData(ok ? (res.data as DasboardDto) : null);
    } catch {
      if (seq !== priorYearSeq.current) return;
      setPriorYearData(null);
    }
  }, [dashboardYear]);

  useEffect(() => {
    void dispatch(fetchApplicationYearsThunk()).then((data) => {
      handleApiResponse(data as never, false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once on mount
  }, [dispatch]);

  useEffect(() => {
    void dispatch(fetchDashboardThunk(dashboardYear)).then((data) => {
      handleApiResponse(data as never, false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- year + dispatch only
  }, [dashboardYear, dispatch]);

  useEffect(() => {
    void loadPriorYear();
  }, [loadPriorYear]);

  useEffect(() => {
    if (dashStatus === "idle" && dashboardData) {
      setLastRefreshedAt(new Date());
    }
  }, [dashStatus, dashboardData]);

  const handleRefresh = useCallback(() => {
    void dispatch(fetchDashboardThunk(dashboardYear)).then((data) => {
      handleApiResponse(data as never, false);
    });
    void loadPriorYear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardYear, dispatch, loadPriorYear]);

  return (
    <>
      <MessageResponse feedback={feedback} />
      <Stack
        direction="row"
        spacing={1}
        className="no-print"
        justifyContent="flex-end"
        sx={{ mb: 2 }}
        flexWrap="wrap"
      >
        <Button
          size="small"
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={() => {
            if (dashboardData) {
              exportDashboardSnapshotCsv(dashboardData, dashboardYear);
            }
          }}
          disabled={!dashboardData}
        >
          Export CSV
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={() => window.print()}
        >
          Print
        </Button>
      </Stack>
      <div id="dashboard-print-root">
        <ApplicationStatusReport
          dashboardYear={dashboardYear}
          onDashboardYearChange={setDashboardYear}
          comparisonData={priorYearData}
          onRefresh={handleRefresh}
          lastRefreshedAt={lastRefreshedAt}
        />
        <DisplayCharts selectedYear={dashboardYear} />
      </div>
    </>
  );
}

export default Dashboard;
